import type { CaptureResult, PostHog } from 'posthog-js';

import { scrubCliAuthFromPostHogEvent } from './scrub-cli-auth-url';

export type InitPostHogOptions = {
  api_key: string;
  api_host: string;
  person_profiles?: 'identified_only' | 'always';
  opt_out_capturing_by_default?: boolean;
  debug?: boolean;
  bootstrap_distinct_id?: string;
  /** `'history_change'` counts each address a single-page app moves to as a page view. */
  capture_pageview?: boolean | 'history_change';
  /** Keeps the text of clicked elements out of autocapture. */
  mask_all_text?: boolean;
  /** Keeps clicked elements' attributes (aria-label, href…) out of autocapture. */
  mask_all_element_attributes?: boolean;
  disable_session_recording?: boolean;
  loaded?: (posthog: PostHog) => void;
};

export type PostHogInit = (api_key: string, options: Record<string, unknown>) => PostHog;

export const initPostHog = (
  posthog_init: PostHogInit,
  options: InitPostHogOptions
): PostHog => {
  return posthog_init(options.api_key, {
    api_host: options.api_host,
    person_profiles: options.person_profiles ?? 'identified_only',
    opt_out_capturing_by_default: options.opt_out_capturing_by_default ?? false,
    debug: options.debug ?? false,
    bootstrap: options.bootstrap_distinct_id
      ? { distinctID: options.bootstrap_distinct_id }
      : undefined,
    before_send: (event: CaptureResult | null) => scrubCliAuthFromPostHogEvent(event),
    loaded: options.loaded,
    // Only when given: an undefined value would replace PostHog's own default.
    ...(options.capture_pageview !== undefined
      ? { capture_pageview: options.capture_pageview }
      : {}),
    ...(options.mask_all_text !== undefined ? { mask_all_text: options.mask_all_text } : {}),
    ...(options.mask_all_element_attributes !== undefined
      ? { mask_all_element_attributes: options.mask_all_element_attributes }
      : {}),
    ...(options.disable_session_recording !== undefined
      ? { disable_session_recording: options.disable_session_recording }
      : {}),
  });
};
